// Central application state (Vue reactivity). Heavy objects (PDF.js document
// proxies, decoded images) are kept in a non-reactive Map so Vue doesn't try to
// deeply observe them; only serialisable metadata is reactive.
import { reactive } from 'vue'
import type { PageComment, SnipDocument, SnipMode, Snippet } from '../types'
import { loadPdf, type PdfDocument } from './pdf'
import { renderDocx, type DocxPage } from './docx'
import { loadState, saveState, saveDocSource, loadDocSource, removeDocSource } from './persistence'

function fileToDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = () => reject(new Error('read failed'))
    r.readAsDataURL(file)
  })
}

function dataUrlToImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('decode failed'))
    img.src = dataUrl
  })
}

interface LoadedSource {
  kind: 'pdf' | 'image' | 'docx'
  pdf?: PdfDocument
  image?: HTMLImageElement
  pages?: DocxPage[] // rendered .docx pages (raster + text layer)
}

const sources = new Map<string, LoadedSource>()

export interface AppState {
  ready: boolean
  insideOffice: boolean
  documents: SnipDocument[]
  snippets: Snippet[]
  comments: PageComment[]
  commentMode: boolean
  showComments: boolean
  activeDocId: string | null
  activePage: number
  zoom: number
  mode: SnipMode
  euNotation: boolean
  busy: boolean
  status: string
  highlightedSnippetId: string | null
  rotation: number // current view rotation in degrees (0/90/180/270)
  sourcesVersion: number // bumped when the (non-reactive) sources map changes, to trigger re-render
}

export const state = reactive<AppState>({
  ready: false,
  insideOffice: false,
  documents: [],
  snippets: [],
  comments: [],
  commentMode: false,
  showComments: true,
  activeDocId: null,
  activePage: 1,
  zoom: 1.25,
  mode: 'text',
  euNotation: true,
  busy: false,
  status: 'Ready',
  highlightedSnippetId: null,
  rotation: 0,
  sourcesVersion: 0,
})

/** Rotate the current view by ±90°. */
export function rotate(delta: number): void {
  state.rotation = (((state.rotation + delta) % 360) + 360) % 360
}

/** Reveal a snippet in the viewer (used by cell→snip sync and the snippet list). */
export function revealSnippet(snippetId: string): void {
  const snip = state.snippets.find((s) => s.id === snippetId)
  if (!snip) return
  if (snip.documentId !== state.activeDocId) state.activeDocId = snip.documentId
  state.activePage = snip.pageNumber
  state.highlightedSnippetId = snippetId
}

let idCounter = 0
function newId(prefix: string): string {
  idCounter += 1
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`
}

export function getSource(docId: string): LoadedSource | undefined {
  return sources.get(docId)
}

export function activeDocument(): SnipDocument | undefined {
  return state.documents.find((d) => d.id === state.activeDocId)
}

export function snippetsForActive(): Snippet[] {
  return state.snippets.filter((s) => s.documentId === state.activeDocId)
}

export async function hydrate(): Promise<void> {
  const persisted = await loadState()
  state.documents = persisted.documents
  state.snippets = persisted.snippets
  state.comments = persisted.comments ?? []
  // Embedded sources (if any) are reloaded by restoreSources(); otherwise re-import is needed.
}

/** Reload embedded document sources from the workbook so pages render without re-importing. */
export async function restoreSources(): Promise<void> {
  for (const doc of state.documents) {
    if (sources.has(doc.id)) continue
    const dataUrl = await loadDocSource(doc.id)
    if (!dataUrl) continue
    try {
      if (doc.kind === 'pdf') {
        const buf = await (await fetch(dataUrl)).arrayBuffer()
        const pdf = await loadPdf(buf)
        sources.set(doc.id, { kind: 'pdf', pdf })
      } else if (doc.kind === 'docx') {
        const buf = await (await fetch(dataUrl)).arrayBuffer()
        const pages = await renderDocx(buf)
        sources.set(doc.id, { kind: 'docx', pages })
      } else {
        const image = await dataUrlToImage(dataUrl)
        sources.set(doc.id, { kind: 'image', image })
      }
    } catch {
      /* leave for manual re-import */
    }
  }
  state.sourcesVersion++
}

// Writes now go to a Custom XML Part (an Excel.run round-trip), so rapid edits are debounced
// and coalesced — echoing the original add-in's batched-save behaviour. flushPersist() forces
// the pending write out immediately (pane hide / unload) so nothing is lost.
let persistTimer: ReturnType<typeof setTimeout> | null = null

function persist(): void {
  if (persistTimer) clearTimeout(persistTimer)
  persistTimer = setTimeout(() => {
    persistTimer = null
    void saveState({ documents: state.documents, snippets: state.snippets, comments: state.comments })
  }, 400)
}

export function flushPersist(): void {
  if (persistTimer) {
    clearTimeout(persistTimer)
    persistTimer = null
  }
  void saveState({ documents: state.documents, snippets: state.snippets, comments: state.comments })
}

export function commentsForActivePage(): PageComment[] {
  return state.comments.filter(
    (c) => c.documentId === state.activeDocId && c.pageNumber === state.activePage,
  )
}

export function addPageComment(x: number, y: number, author: string): PageComment {
  const c: PageComment = {
    id: newId('cmt'),
    documentId: state.activeDocId ?? '',
    pageNumber: state.activePage,
    x,
    y,
    text: '',
    author,
    createdAt: Date.now(),
    rotation: state.rotation,
  }
  state.comments.push(c)
  persist()
  return c
}

export function updatePageComment(id: string, text: string): void {
  const c = state.comments.find((x) => x.id === id)
  if (!c) return
  c.text = text
  persist()
}

export function removePageComment(id: string): void {
  state.comments = state.comments.filter((c) => c.id !== id)
  persist()
}

const IMAGE_EXT = /\.(png|jpe?g|bmp|gif|tiff?|webp)$/i

export async function importFiles(files: FileList | File[]): Promise<void> {
  const list = Array.from(files)
  for (const file of list) {
    state.busy = true
    state.status = `Importing ${file.name}…`
    try {
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
      const isDocx =
        /\.docx$/i.test(file.name) ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      const isImage = IMAGE_EXT.test(file.name) || file.type.startsWith('image/')
      if (!isPdf && !isDocx && !isImage) {
        // .doc (legacy binary) isn't supported by the client-side renderer — only .docx (OOXML).
        state.status = /\.doc$/i.test(file.name)
          ? `Legacy .doc isn't supported — save as .docx and re-import: ${file.name}`
          : `Unsupported file type: ${file.name}`
        continue
      }

      // Re-importing a file with the same name reuses the existing document (and its id) so that
      // snippets persisted against it re-link, instead of creating a duplicate document.
      const existing = state.documents.find((d) => d.name === file.name)
      const id = existing ? existing.id : newId('doc')

      if (isPdf) {
        const buf = await file.arrayBuffer()
        const pdf = await loadPdf(buf)
        sources.set(id, { kind: 'pdf', pdf })
        if (existing) {
          existing.pageCount = pdf.numPages
          existing.kind = 'pdf'
        } else {
          state.documents.push({ id, name: file.name, kind: 'pdf', pageCount: pdf.numPages, dateAdded: Date.now() })
        }
      } else if (isDocx) {
        state.status = `Rendering ${file.name}…`
        const buf = await file.arrayBuffer()
        const pages = await renderDocx(buf)
        sources.set(id, { kind: 'docx', pages })
        if (existing) {
          existing.pageCount = pages.length
          existing.kind = 'docx'
        } else {
          state.documents.push({ id, name: file.name, kind: 'docx', pageCount: pages.length, dateAdded: Date.now() })
        }
      } else {
        const image = await loadImage(file)
        sources.set(id, { kind: 'image', image })
        if (existing) {
          existing.pageCount = 1
          existing.kind = 'image'
        } else {
          state.documents.push({ id, name: file.name, kind: 'image', pageCount: 1, dateAdded: Date.now() })
        }
      }

      // Embed the source in the workbook so its pages survive reopen (no re-import needed).
      // Size-guarded — large files fall back to the re-import flow.
      try {
        const dataUrl = await fileToDataUrl(file)
        const embedded = await saveDocSource(id, dataUrl)
        const doc = state.documents.find((d) => d.id === id)
        if (doc) doc.embedded = embedded
      } catch {
        /* embedding is best-effort */
      }

      state.activeDocId = id
      state.activePage = 1
    } catch (err) {
      state.status = `Failed to import ${file.name}: ${(err as Error).message}`
    } finally {
      state.busy = false
    }
  }
  persist()
  state.status = 'Ready'
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      // The bitmap is decoded into memory now; the blob URL can be released.
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not decode image'))
    }
    img.src = url
  })
}

export function setActiveDoc(id: string): void {
  state.activeDocId = id
  state.activePage = 1
  state.rotation = 0
}

export function setPage(n: number): void {
  const doc = activeDocument()
  if (!doc) return
  state.activePage = Math.min(Math.max(1, n), doc.pageCount)
}

export function setMode(mode: SnipMode): void {
  state.mode = mode
}

export function setZoom(z: number): void {
  state.zoom = Math.min(Math.max(0.4, z), 4)
}

export function addSnippet(s: Omit<Snippet, 'id' | 'createdAt'>): Snippet {
  const snippet: Snippet = { ...s, id: newId('snip'), createdAt: Date.now() }
  state.snippets.push(snippet)
  persist()
  return snippet
}

export function setSnippetName(id: string, name: string): void {
  const snip = state.snippets.find((s) => s.id === id)
  if (!snip) return
  snip.displayName = name
  persist()
}

export function setSnippetComment(id: string, comment: string): void {
  const snip = state.snippets.find((s) => s.id === id)
  if (!snip) return
  snip.comment = comment.trim() || undefined
  persist()
}

export function removeSnippet(id: string): Snippet | undefined {
  const idx = state.snippets.findIndex((s) => s.id === id)
  if (idx === -1) return undefined
  const [removed] = state.snippets.splice(idx, 1)
  persist()
  return removed
}

export function snippetCountForDoc(docId: string): number {
  return state.snippets.filter((s) => s.documentId === docId).length
}

export function snippetCountForPage(docId: string, page: number): number {
  return state.snippets.filter((s) => s.documentId === docId && s.pageNumber === page).length
}

/** Delete all snippets of a document (leaves Excel cell contents intact, like single delete). */
export function removeSnippetsForDocument(docId: string): void {
  state.snippets = state.snippets.filter((s) => s.documentId !== docId)
  persist()
}

export function removeSnippetsForPage(docId: string, page: number): void {
  state.snippets = state.snippets.filter((s) => !(s.documentId === docId && s.pageNumber === page))
  persist()
}

export function removeDocument(id: string): void {
  state.documents = state.documents.filter((d) => d.id !== id)
  state.snippets = state.snippets.filter((s) => s.documentId !== id)
  // Release the PDF.js worker-side document before dropping the reference.
  void sources.get(id)?.pdf?.destroy()
  sources.delete(id)
  void removeDocSource(id)
  if (state.activeDocId === id) {
    state.activeDocId = state.documents[0]?.id ?? null
    state.activePage = 1
  }
  persist()
}
